import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Wallet, Lightbulb, FileText, GitCompare, Plane, Sparkles, X,
  Target, MessageSquare, ArrowRight, Bookmark,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

const SEEN_KEY = "evermin_onboarded";
const INTEREST_KEY = "evermin_interest";

const OPTIONS = [
  { id: "money", label: "ob_money", icon: Wallet, tone: "text-emerald-600 bg-emerald-50" },
  { id: "problems", label: "ob_problems", icon: Lightbulb, tone: "text-amber-600 bg-amber-50" },
  { id: "documents", label: "ob_docs", icon: FileText, tone: "text-sky-600 bg-sky-50" },
  { id: "compare", label: "ob_compare", icon: GitCompare, tone: "text-indigo-600 bg-indigo-50" },
  { id: "travel", label: "ob_travel", icon: Plane, tone: "text-rose-600 bg-rose-50" },
  { id: "everything", label: "ob_everything", icon: Sparkles, tone: "text-primary bg-primary/10" },
];

// A premium, animated mockup of the ACTUAL live My EVERMIN dashboard. Pure
// CSS/icons so it loads instantly and always reflects real sections.
function DashboardPreview({ t }) {
  const cards = [
    { icon: Wallet, tone: "text-emerald-600 bg-emerald-50", label: t("ml_money"), lines: ["EMI · ₹16,801/mo", "GST · ₹2,950"] },
    { icon: Target, tone: "text-indigo-600 bg-indigo-50", label: t("ml_goals"), lines: ["Emergency fund", "₹2,40,000"] },
    { icon: Plane, tone: "text-amber-600 bg-amber-50", label: t("ml_plans"), lines: ["Goa trip", "3-day plan"] },
  ];
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-b from-slate-50 to-white p-3.5 shadow-inner sm:p-4" data-testid="onboarding-preview">
      <div className="mb-2.5 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></span>
        <span className="font-display text-xs font-bold text-slate-800">{t("ml_teaser_preview_label")}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {cards.map(({ icon: Icon, tone, label, lines }, idx) => (
          <div key={label} style={{ animationDelay: `${idx * 90}ms` }}
            className="animate-in fade-in slide-in-from-bottom-2 rounded-xl border border-slate-200 bg-white/90 p-2.5 backdrop-blur duration-500">
            <span className={`mb-1.5 flex h-6 w-6 items-center justify-center rounded-lg ${tone}`}><Icon className="h-3.5 w-3.5" /></span>
            <p className="truncate text-[11px] font-semibold text-slate-800">{label}</p>
            {lines.map((l) => <p key={l} className="mt-0.5 truncate text-[10px] text-slate-400">{l}</p>)}
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div style={{ animationDelay: "270ms" }} className="animate-in fade-in slide-in-from-bottom-2 flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white/90 p-2.5 duration-500">
          <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          <span className="truncate text-[10px] font-medium text-slate-500">{t("recent_conversations")}</span>
        </div>
        <div style={{ animationDelay: "340ms" }} className="animate-in fade-in slide-in-from-bottom-2 flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white/90 p-2.5 duration-500">
          <Bookmark className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          <span className="truncate text-[10px] font-medium text-slate-500">{t("ml_library")}</span>
        </div>
      </div>
    </div>
  );
}

export function Onboarding() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1); // 1 teaser · 2 question · 3 build

  useEffect(() => {
    // First-time, logged-out visitors on the homepage only. A small delay lets
    // auth resolve first so returning/logged-in users never see a flash.
    if (user || pathname !== "/" || localStorage.getItem(SEEN_KEY)) return;
    const id = setTimeout(() => {
      if (!localStorage.getItem(SEEN_KEY)) setVisible(true);
    }, 700);
    return () => clearTimeout(id);
  }, [user, pathname]);

  // If the visitor logs in while it's open, get out of the way immediately.
  useEffect(() => { if (user) setVisible(false); }, [user]);

  if (!visible) return null;

  const done = () => { try { localStorage.setItem(SEEN_KEY, "1"); } catch (e) {} setVisible(false); };
  const pick = (id) => {
    try { localStorage.setItem(INTEREST_KEY, id); } catch (e) {}
    setStep(3);
  };
  const goAuth = (path) => { done(); navigate(path); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="onboarding-overlay">
      <div className="animate-in slide-in-from-bottom-4 fade-in relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-6 shadow-premium-lg duration-300 sm:rounded-3xl sm:p-8">
        <button onClick={done} data-testid="onboarding-skip-x" aria-label={t("ob_skip")}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-slate-400 backdrop-blur hover:bg-slate-100 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>

        {step === 1 && (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-testid="onboarding-badge">
              <Sparkles className="h-3.5 w-3.5" /> My EVERMIN
            </span>
            <h2 className="font-display mt-3 pr-6 text-2xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-3xl" data-testid="onboarding-headline">
              {t("ml_teaser_headline")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{t("ml_teaser_sub")}</p>

            <div className="mt-5"><DashboardPreview t={t} /></div>

            <p className="mt-5 text-center text-sm font-medium text-slate-600">{t("ml_teaser_make_yours")}</p>
            <div className="mt-3 space-y-2.5">
              <Button onClick={() => goAuth("/signup")} data-testid="onboarding-signup"
                className="h-12 w-full rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800">
                {t("ml_teaser_cta")}
              </Button>
              <Button onClick={() => goAuth("/login")} variant="outline" data-testid="onboarding-login"
                className="h-12 w-full rounded-xl font-semibold">
                {t("ob_login")}
              </Button>
            </div>
            <p className="mt-2.5 text-center text-xs text-slate-400">{t("ml_teaser_no_payment")}</p>

            <button onClick={() => setStep(2)} data-testid="onboarding-personalize"
              className="mt-4 flex w-full items-center justify-center gap-1 text-sm font-semibold text-primary hover:underline">
              {t("ml_teaser_personalize")} <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button onClick={done} data-testid="onboarding-skip" className="mt-3 w-full text-center text-sm font-medium text-slate-400 hover:text-slate-600">
              {t("ob_skip")}
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="font-display pr-8 text-xl font-extrabold tracking-tight text-slate-950 sm:text-2xl" data-testid="onboarding-question">
              {t("ob_question")}
            </h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {OPTIONS.map(({ id, label, icon: Icon, tone }) => (
                <button key={id} onClick={() => pick(id)} data-testid={`onboarding-option-${id}`}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-premium">
                  <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span>
                  <span className="text-sm font-semibold leading-tight text-slate-900">{t(label)}</span>
                </button>
              ))}
            </div>
            <button onClick={done} data-testid="onboarding-skip" className="mt-5 w-full text-center text-sm font-medium text-slate-400 hover:text-slate-600">
              {t("ob_skip")}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-6 w-6" /></span>
            <h2 className="font-display mt-4 text-xl font-extrabold tracking-tight text-slate-950 sm:text-2xl" data-testid="onboarding-perfect">{t("ob_build")}</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">{t("ml_teaser_make_yours")}</p>
            <div className="mt-6 space-y-2.5">
              <Button onClick={() => goAuth("/signup")} data-testid="onboarding-signup-2" className="h-12 w-full rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800">
                {t("ml_teaser_cta")}
              </Button>
              <Button onClick={() => goAuth("/login")} variant="outline" data-testid="onboarding-login-2" className="h-12 w-full rounded-xl font-semibold">
                {t("ob_login")}
              </Button>
            </div>
            <p className="mt-2.5 text-xs text-slate-400">{t("ml_teaser_no_payment")}</p>
            <button onClick={done} data-testid="onboarding-skip-2" className="mt-4 w-full text-center text-sm font-medium text-slate-400 hover:text-slate-600">
              {t("ob_skip")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
